import { useEffect, useState } from 'react';

import { useQueryClient } from 'react-query';
import { generatePath, useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { useSubscription } from 'react-stomp-hooks';
import { isStringHasRealContent } from 'utilitys/helpers/general';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';
import useUserHook from 'hooks/useUserHook';

import { useCommentsStore } from 'store/comments.store';
import { useCommentsEditorStatus } from 'store/commentsEditorStatus.store';

import { useMutationCreateEditComment } from 'api/mutations/createEditComment.mutation';
import { useMutationDeleteComment } from 'api/mutations/deleteComment.mutation';
import { useQueryGetCardComments } from 'api/queries/getCardComments.query';

import { IComment } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/battlecards.types';

const sortCommentsData = (arr) => {
    return arr.sort(function (a, b) {
        return -(a.commentId - b.commentId);
    });
};

export const useCommentProvider = ({ id, isVirtual, queryKeys }) => {
    const commentData = useCommentsStore((store) => store.commentData);
    const commentId = useCommentsStore((store) => store.commentId);
    const { setCommentData, setCommentId } = useCommentsStore();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { commentsEditorStatus, resetCommentsEditorStatus, setCommentsEditorStatus } = useCommentsEditorStatus();
    const queryclient = useQueryClient();

    const { firstName, lastName, username, ability, email, image } = useUserHook();
    const [deleteCommentModalShow, setDeleteCommentModalShow] = useState(false);
    const [removeCommentId, setRemoveCommentId] = useState<number | null>(null);

    const { handleSnackBar } = useCustomSnackBar();

    const [commentError, setCommentError] = useState(false);
    const { sendEventWithDimensions } = useEventWithDimensions();

    const history = useHistory();
    const { path } = useRouteMatch();
    const params = useParams();
    const { search } = useLocation();

    const { data: cardCommentData } = useQueryGetCardComments({ id, isVirtual });
    const { mutate: createEditCommentMutate } = useMutationCreateEditComment();
    const { mutate: deleteCommentMutate } = useMutationDeleteComment();
    /**
     * if trying to save card when comment editor is open, mark red border as error accor
     */
    useEffect(() => {
        if (commentsEditorStatus.isError) {
            setCommentError(true);
        }
    }, [commentsEditorStatus.isError]);

    /**
     * update id and isVirtual
     */
    useEffect(() => {
        if (id && typeof isVirtual !== 'undefined') {
            setCommentData({ ...commentData, cardId: id, isVirtual: isVirtual });
        }
    }, [id, isVirtual]);

    /**
     * displays the comments editor
     */
    const setAddCommentMode = (value: string) => {
        setCommentData({ ...commentData, cardId: id, isVirtual: isVirtual, id: null, comment: value });
        setCommentId(false);
        setCommentError(false);

        // set to zustand store if comments editor is open or close
        // for the save card button, if editor is open don't allow save card
        setCommentsEditorStatus({
            isOpen: true,
            isError: false,
        });
    };

    /**
     * hides the comment editor
     */
    const cancelAddCommentMode = () => {
        setCommentData({ ...commentData, comment: '', mentionedList: [] });
        resetCommentsEditorStatus();
    };

    /**
     * updates the comment value
     * @param value
     * @param mentionedList
     */
    const handleEditorDescription = (value, mentionedList) => {
        setCommentData({ ...commentData, comment: value, mentionedList });
    };

    /**
     * dispatch an action to create the comment
     * 1. checks if comment value is empty after striping HTML tags that was added from the editor
     * 2. if its empty - returns false
     * 3. if card is virtual (card that was created by the BE based on tasks in the version) it runs a callback to create the card and then add the comment data since virtual cards are handling the comment data separately
     * 4. if card is not virtual - dispatch create comment (create and update are in the same API call)
     */
    const createComment = () => {
        const tempSingleCommentData = { ...commentData };

        if (isStringHasRealContent(tempSingleCommentData.comment)) {
            setCommentError(true);
            return;
        }

        setIsLoading(true);
        createEditCommentMutate(tempSingleCommentData, {
            onSuccess: (res) => {
                handleSnackBar('Comment Successfully Saved', 'success');

                return Promise.all([
                    ...queryKeys.map((key) => queryclient.invalidateQueries(key)),
                    queryclient.invalidateQueries('getCardComment'),
                ]).then(() => {
                    setIsLoading(false);
                    resetCommentsEditorStatus();
                    setCommentData({ ...commentData, cardId: res.cardId, isVirtual: false, comment: '' });

                    const link = `${generatePath(path, { ...params, cardId: res.cardId })}${search}`;
                    history.push(link);
                });
            },
            onError: (error) => {
                DEFAULT_ERROR({ error, handleError: handleSnackBar });
                setIsLoading(false);
            },
        });

        sendEventWithDimensions({
            category: 'Battlecards',
            action: 'Add Comment',
            label: String(tempSingleCommentData.cardId),
        });
    };

    /**
     * dispatch an action to update a comment
     * 1. checks if comment value is empty after striping HTML tags that was added from the editor
     * 2. if its empty - returns false
     * 3. set the id to the tempComment object
     * 4. dispatch update comment (create and update are in the same API call)
     */
    const updateComment = (commentId) => {
        const tempSingleCommentData = { ...commentData, cardId: id };

        if (isStringHasRealContent(tempSingleCommentData.comment)) {
            setCommentError(true);
            return;
        }

        setIsLoading(true);
        tempSingleCommentData.id = commentId;
        createEditCommentMutate(tempSingleCommentData, {
            onSuccess: (res) => {
                handleSnackBar('Comment Successfully Updated', 'success');

                return Promise.all([
                    ...queryKeys.map((key) => queryclient.invalidateQueries(key)),
                    queryclient.invalidateQueries('getCardComment'),
                ]).then(() => {
                    resetCommentsEditorStatus();
                    setCommentId(false);
                    setIsLoading(false);
                    setCommentData({ ...commentData, cardId: res.cardId, isVirtual: false, comment: '' });
                });
            },
            onError: (error) => {
                DEFAULT_ERROR({ error, handleError: handleSnackBar });
                setIsLoading(false);
            },
        });

        sendEventWithDimensions({
            category: 'Battlecards',
            action: 'Update Comment',
            label: tempSingleCommentData.cardId,
        });
    };

    const confirmDeleteComment = (id: number) => {
        setRemoveCommentId(id);
        setDeleteCommentModalShow(true);
    };

    const handleCommentDelete = (id: number) => {
        setDeleteCommentModalShow(false);
        deleteCommentMutate(id, {
            onSuccess: () => {
                handleSnackBar('Comment Successfully Deleted', 'success');

                return Promise.all([
                    ...queryKeys.map((key) => queryclient.invalidateQueries(key)),
                    queryclient.invalidateQueries('getCardComment'),
                ]);
            },
            onError: (error) => {
                DEFAULT_ERROR({ error, handleError: handleSnackBar });
            },
        });
    };

    const sortedComments: IComment[] = cardCommentData ? sortCommentsData([...cardCommentData]) : [];

    // AI Mitigation Section ====================================================================================
    const isAIMitigationPrivilege = ability.can('view', 'AIMitigation');
    const [isLoadingMitigation, setIsLoadingMitigation] = useState(false);

    useSubscription(email ? [`/topic/${email}.notifications.push`] : [], (response) => {
        const { type } = JSON.parse(response.body);
        if (type === 'AiMitigation') {
            handleSnackBar('Generative-AI response is ready', 'success');

            Promise.all([
                ...queryKeys.map((key) => queryclient.invalidateQueries(key)),
                queryclient.invalidateQueries('getCardComment'),
            ]).then(() => {
                setIsLoadingMitigation(false);
                setCommentId(false);
            });
        }
    });

    return {
        cancelAddCommentMode,
        commentData,
        commentError,
        commentId,
        commentsEditorStatus,
        confirmDeleteComment,
        createComment,
        deleteCommentModalShow,
        email,
        firstName,
        handleCommentDelete,
        handleEditorDescription,
        image,
        isAIMitigationPrivilege,
        isLoading,
        isLoadingMitigation,
        lastName,
        removeCommentId,
        resetCommentsEditorStatus,
        setAddCommentMode,
        setCommentData,
        setCommentError,
        setCommentId,
        setDeleteCommentModalShow,
        setIsLoadingMitigation,
        sortedComments,
        updateComment,
        username,
    };
};
